/**
 * What a drag can be over.
 *
 * A **sortable** (`data-sort-id`) is one of the things being arranged. A
 * **drop zone** (`data-drop-id`) is somewhere declared to receive them — a
 * crumb in a heading — which can be held over but never reordered into.
 */
export type Held = { id: string; kind: 'sort' | 'drop' };

/**
 * The middle of a sortable, as a fraction of it. Kept under half so that most
 * of a tile still means "put it here" — only a deliberate aim at the centre
 * means "put it in there".
 */
const CENTRE = 0.45;

/**
 * What's under the point, and — for a sortable — whether it's the middle.
 * `skip` is the thing being dragged: it can't be dropped on itself, and its
 * own lifted node has no pointer events to be found by anyway.
 */
export function targetAt(
  x: number,
  y: number,
  skip: string,
  among: readonly string[],
): (Held & { middle: boolean }) | undefined {
  for (const element of document.elementsFromPoint(x, y)) {
    // Declared drop zones win. They're small and deliberate, and something
    // dragged over one is being aimed at it — so the whole of one counts as
    // its middle rather than only a band inside it.
    const zone = element.closest<HTMLElement>('[data-drop-id]')?.dataset.dropId;
    if (zone !== undefined) return { id: zone, kind: 'drop', middle: true };

    const box = element.closest<HTMLElement>('[data-sort-id]');
    const found = box?.dataset.sortId;
    if (box === null || box === undefined) continue;
    if (found === undefined || found === skip || !among.includes(found)) continue;

    const rect = box.getBoundingClientRect();
    const inset = (1 - CENTRE) / 2;
    return {
      id: found,
      kind: 'sort',
      middle:
        x > rect.left + rect.width * inset &&
        x < rect.right - rect.width * inset &&
        y > rect.top + rect.height * inset &&
        y < rect.bottom - rect.height * inset,
    };
  }

  return undefined;
}
